<template>
  <div class="cfs-autocomplete">
    <div
      v-if="!readonly"
      class="cfs-field d-flex flex-column"
      :class="[selectClass, ...(staticBg ? ['static-bg'] : [])]"
    >
      <p v-if="label" class="input-title px-1 pb-1" :class="{ disabled }">
        <slot name="label" :label="label">
          {{ label }}
        </slot>
      </p>
      <v-autocomplete
        dense
        :outlined="outlined"
        :items="computedItems"
        :multiple="multiple"
        :value="value"
        v-bind="{ ...$props, ...$attrs }"
        :search-input.sync="searchQuery"
        :placeholder="placeholder || label"
        :label="null"
        v-on="$listeners"
        @blur="onBlur"
      >
        <template v-if="isLoading" #append>
          <v-progress-circular :size="20" color="primary" class="mr-2" indeterminate />
        </template>
        <template v-if="$slots.item" #item="{ item }">
          <slot v-bind="item" name="item" />
        </template>
        <template v-for="(_, name) in $scopedSlots" #[name]="slotData">
          <slot :name="name" v-bind="slotData" />
        </template>
      </v-autocomplete>
      <small v-if="hint.length && disabled" class="pl-2 text-blue-havelock"> * {{ hint }} </small>
    </div>
    <CfsReadonlyField
      v-else
      v-bind="$props"
      :key="valueStr"
      v-model="valueStr"
      :loading="isLoading"
    >
      <template v-if="slots.label" #label="slotProps">
        <slot name="label" v-bind="slotProps">
          {{ label }}
        </slot>
      </template>
    </CfsReadonlyField>
  </div>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, PropType, ref } from 'vue';
import { isObject } from '@/helpers/objects';
import { props } from '@/composables/icons/useIcon';

export default defineComponent({
  components: {
    CfsReadonlyField: defineAsyncComponent(() => import('./special/CfsReadonlyFieldComponent.vue')),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    selectClass: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    itemValue: {
      type: String,
      default: 'code',
    },
    itemText: {
      type: [String, Function],
      default: 'description',
    },
    /**when set to false fills input with application background solid color instead of inherited one.
     * I.e. removes transparency from input*/
    staticBg: {
      type: Boolean,
      default: true,
    },
    returnObject: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Object, Array],
      default: null,
    },
    items: {
      type: Array as PropType<Record<string, unknown>[]>,
      default: () => [],
    },
    additionalOptions: {
      type: Array,
      default: () => [],
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    hint: {
      type: String,
      default: '',
    },
  },
  setup(props, { slots }) {
    const searchQuery = ref<string>('');
    const onBlur = () => {
      if (!props.multiple) return;
      searchQuery.value = '';
    };
    const valueStr = computed(() => {
      if (!props.value) {
        return null;
      }

      const item = props.items?.find((item) => {
        if (isObject(props.value)) {
          return (
            item?.[props?.itemValue] === (props.value as Record<string, unknown>)[props?.itemValue]
          );
        }
        return item?.[props?.itemValue] === props.value;
      });

      if (!item) {
        // @ts-ignore
        return props.value[props.itemText];
      }
      if (typeof props.itemText === 'function') {
        return props.itemText(item);
      }

      return item ? item[props.itemText] : props.value;
    });

    const computedItems = computed(() => {
      const sortItems = () => {
        const items = props.items.slice();
        try {
          if (typeof props.itemText === 'string') {
            return items.sort((a, b) => a?.[props.itemText]?.localeCompare(b?.[props.itemText]));
          }
          if (typeof props.itemText === 'function') {
            return items.sort((a, b) => props.itemText(a)?.localeCompare(props.itemText(b)));
          }
        } catch (e) {
          console.log(e);
        }
        return items;
      };

      return [...props.additionalOptions, ...sortItems()];
    });

    return { searchQuery, computedItems, valueStr, onBlur, slots };
  },
});
</script>

<style lang="scss" scoped>
.cfs-autocomplete {
  &:not(.no-chip):deep(.v-chip) {
    height: 28px;
    margin: 2px;
    font-weight: 500;
    border-radius: 8px;
    background: rgba(30, 118, 189, 0.2);
  }
  &:deep(.v-select__selections) {
    flex-wrap: nowrap !important;
  }

  &:deep(.v-select--chips) input {
    max-height: 32px;
  }
}
</style>
